import { useState, useMemo } from "react";
import type {
  TemplateTask,
  TemplateStep,
  LookupValue,
  Team,
} from "@/types/task";
import type { UserProfile } from "@/types/user";
import { TbPlus, TbEdit, TbTrash, TbEye, TbSearch } from "react-icons/tb";
import { useTemplateStore } from "@/stores/templateStore";
import { useModalStore } from "@/stores/modalStore";
import { TemplateForm } from "./TemplateForm";
import { TemplateViewModal } from "./TemplateViewModal";
import { getFigmaPriorityStyles } from "@/lib/priorityColors";

interface TemplateData {
  name: string;
  description: string;
  layer_name: string;
  category_id: string;
  status_id: string;
  priority_id: string;
  default_assignee_type?: "User" | "Team";
  default_assignee?: string;
  default_due_days?: number;
  default_start_days?: number;
  auto_assign_steps?: boolean;
  require_photos?: boolean;
  require_geometry?: boolean;
  allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
}

interface TemplateManagerProps {
  templates: TemplateTask[];
  categories: LookupValue[];
  statuses: LookupValue[];
  priorities: LookupValue[];
  users: UserProfile[];
  teams: Team[];
  onCreateTemplate: (
    templateData: TemplateData,
    templateSteps: TemplateStep[]
  ) => Promise<void>;
  onUpdateTemplate: (
    templateId: string,
    templateData: TemplateData,
    templateSteps: TemplateStep[]
  ) => Promise<void>;
  onDeleteTemplate: (templateId: string) => void;
  error?: string | null;
}

export const TemplateManager: React.FC<TemplateManagerProps> = ({
  templates,
  categories,
  statuses,
  priorities,
  users,
  teams,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  error,
}) => {
  // Zustand store
  const {
    newTemplateData,
    templateSteps,
    startCreateTemplate,
    startEditTemplate,
    resetTemplateForm,
    validateTemplateData,
  } = useTemplateStore();

  // Modal state from modalStore
  const { showCreateTemplate, editingTemplate, openEditTemplate } =
    useModalStore();

  // Local state for search and view modal
  const [viewingTemplate, setViewingTemplate] = useState<TemplateTask | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates;
    return templates.filter((template) =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [templates, searchTerm]);

  const handleSubmit = async () => {
    // Validate template data before submission
    if (!validateTemplateData()) {
      return; // Stop submission if validation fails
    }

    if (editingTemplate) {
      await onUpdateTemplate(
        editingTemplate.id,
        newTemplateData,
        templateSteps
      );
    } else {
      await onCreateTemplate(newTemplateData, templateSteps);
    }

    // Reset form state using Zustand
    resetTemplateForm();
  };

  const handleView = (template: TemplateTask) => {
    setViewingTemplate(template);
  };

  const handleCloseView = () => {
    setViewingTemplate(null);
  };

  const handleEdit = (template: TemplateTask) => {
    // Close view modal if it's open
    setViewingTemplate(null);

    startEditTemplate(template);
    openEditTemplate(template);
  };

  return (
    <div>
      {/* Full Screen Create/Edit Template Form */}
      {showCreateTemplate || editingTemplate ? (
        <TemplateForm
          categories={categories}
          statuses={statuses}
          priorities={priorities}
          users={users}
          teams={teams}
          onSubmit={handleSubmit}
        />
      ) : (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto max-h-[calc(100vh-380px)]">
            <div className="space-y-4 w-full">
              {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}
              {/* Search Bar */}
              <div className="flex flex-col items-start gap-[6px] w-full rounded-lg">
                <div className="flex p-[13px_14px] items-center gap-4 w-full rounded-[10px] bg-[#F3F4F6]">
                  <div className="flex justify-center items-center gap-2 flex-1">
                    <TbSearch className="w-4 h-4 text-[#828282]" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
                    />
                  </div>
                </div>
              </div>

              {/* Templates List */}
              <div className="space-y-3">
                {filteredTemplates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm
                      ? "No templates found matching your search."
                      : "No templates created yet. Create your first template!"}
                  </div>
                ) : (
                  filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="flex justify-between items-start self-stretch p-4 overflow-hidden rounded-2xl border border-[#F3F4F6] outline-1 outline-[#F3F4F6] outline-offset-[-1px] hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col justify-start items-start gap-3">
                        {/* First section: Template name and priority */}
                        <div className="flex flex-col justify-start items-start gap-2">
                          <div className="flex justify-center items-center gap-2.5">
                            <div className="text-[#444444] text-[16px] font-['Open_Sans'] font-bold leading-[22.4px]">
                              {template.name}
                            </div>
                            {template.priority && (
                              <div
                                className="px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 rounded-[20px] flex justify-center items-center gap-2"
                                style={getFigmaPriorityStyles(
                                  template.priority
                                )}
                              >
                                <div className="text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                                  {template.priority.label}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Second line: Description + dot + Created date */}
                          <div className="flex justify-center items-center gap-2">
                            <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
                              {template.description || "Sweep the bridge"}
                            </div>
                            <div className="w-[5px] h-[5px] opacity-30 bg-[#828282] rounded-full"></div>
                            <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
                              Created :{" "}
                              {new Date(template.created_at).toLocaleDateString(
                                "en-GB"
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Third section: Layer and Category pills */}
                        <div className="flex justify-start items-start gap-1.5">
                          {template.layer_name && (
                            <div className="px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 bg-[#F3F4F6] rounded-[20px] flex justify-center items-center gap-2">
                              <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                                Layer: {template.layer_name}
                              </div>
                            </div>
                          )}
                          {template.category && (
                            <div className="px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 bg-[#F3F4F6] rounded-[20px] flex justify-center items-center gap-2">
                              <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                                {template.category.label}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action icons */}
                      <div className="flex justify-start items-start gap-2">
                        <div
                          onClick={() => handleView(template)}
                          className="w-9 h-9 rounded-full border border-gray-100 bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                          title="View Template"
                        >
                          <TbEye size={14} className="text-[#828282]" />
                        </div>
                        <div
                          onClick={() => handleEdit(template)}
                          className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                          title="Edit Template"
                        >
                          <TbEdit size={14} className="text-[#828282]" />
                        </div>
                        <div
                          onClick={() => onDeleteTemplate(template.id)}
                          className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                          title="Delete Template"
                        >
                          <TbTrash size={14} className="text-red-400" />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          {/* Create Template Button */}
          <div className="flex gap-6 w-full">
            <button
              onClick={() => startCreateTemplate()}
              className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
            >
              <TbPlus className="inline w-4 h-4 mr-2" />
              Create Template
            </button>
          </div>
        </>
      )}

      {/* Template View Modal */}
      <TemplateViewModal
        template={viewingTemplate}
        open={!!viewingTemplate}
        onClose={handleCloseView}
      />
    </div>
  );
};
